import { useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { ChevronDown, X } from "lucide-react";

/**
 * Compact multi-select filter that mimics the look of the pipeline's other
 * single-selects (h-11 pill button). Fully controlled — the parent owns the
 * `selected` array and receives updates via `onChange`.
 *
 * Props:
 *  - placeholder  – text shown when nothing is selected (e.g. "All statuses")
 *  - options      – Array<{ value, label }>
 *  - selected     – Array<string> currently selected values
 *  - onChange     – (nextArray) => void
 *  - testid       – base data-testid; the row and clear button derive from it
 */
export function MultiSelectFilter({ placeholder, options, selected, onChange, testid }) {
  const [open, setOpen] = useState(false);

  const summary = useMemo(() => {
    if (!selected || selected.length === 0) return placeholder;
    if (selected.length === 1) {
      const found = options.find((o) => o.value === selected[0]);
      return found ? found.label : "1 selected";
    }
    return `${selected.length} selected`;
  }, [selected, options, placeholder]);

  const toggle = (value) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const clearAll = (e) => {
    e.stopPropagation();
    onChange([]);
  };

  const hasSelection = selected && selected.length > 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="rounded-xl h-11 w-full px-3 text-sm bg-white border border-gray-300 text-brand-navy hover:border-brand-navy/40 focus:border-brand-orange focus:outline-none flex items-center justify-between gap-2"
          data-testid={testid}
        >
          <span className={`truncate ${hasSelection ? "font-medium" : "text-brand-slate/70"}`}>
            {summary}
          </span>
          <span className="flex items-center gap-1 shrink-0">
            {hasSelection && (
              <span
                role="button"
                tabIndex={0}
                onClick={clearAll}
                onKeyDown={(e) => { if (e.key === "Enter") clearAll(e); }}
                className="p-0.5 rounded-full text-brand-slate/60 hover:text-brand-orange hover:bg-brand-cream cursor-pointer"
                aria-label="Clear selection"
                data-testid={`${testid}-clear`}
              >
                <X size={14} />
              </span>
            )}
            <ChevronDown size={14} className="text-brand-slate/60" />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[min(320px,90vw)] p-0" data-testid={`${testid}-panel`}>
        <div className="max-h-72 overflow-y-auto py-1">
          {options.length === 0 && (
            <p className="text-xs text-brand-slate/60 px-3 py-3">No options available</p>
          )}
          {options.map((opt) => {
            const checked = selected.includes(opt.value);
            return (
              <label
                key={opt.value}
                className="flex items-center gap-2.5 px-3 py-2 text-sm text-brand-navy hover:bg-brand-cream/70 cursor-pointer"
                data-testid={`${testid}-option-${opt.value}`}
              >
                <Checkbox
                  checked={checked}
                  onCheckedChange={() => toggle(opt.value)}
                  aria-label={opt.label}
                />
                <span className="truncate">{opt.label}</span>
              </label>
            );
          })}
        </div>
        {hasSelection && (
          <div className="border-t border-brand-navy/10 px-3 py-2 flex items-center justify-between">
            <span className="text-[11px] uppercase tracking-[0.2em] text-brand-slate/60">
              {selected.length} selected
            </span>
            <button
              type="button"
              onClick={() => onChange([])}
              className="text-xs text-brand-orange hover:underline"
              data-testid={`${testid}-clear-all`}
            >
              Clear all
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
